import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import DramaCard from "@/components/base/DramaCard";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import { dramas } from "@/mocks/dramas";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const type = (searchParams.get("type") as "drama" | "news") ?? "drama";
  const page = Number(searchParams.get("page") ?? "1") || 1;
  const [input, setInput] = useState(q);

  useSeo({
    title: q ? `“${q}” 的搜索结果 - 赏金女王影视大全` : "全站搜索 - 赏金女王影视大全",
    description: q
      ? `赏金女王搜索“${q}”的结果，包含相关影视作品与影视资讯。`
      : "在赏金女王影视大全搜索影视作品与影视资讯内容。",
    keywords: "影视搜索,搜索影视,赏金女王搜索,影视资讯搜索",
  });

  const matchedDramas = useMemo(() => {
    const k = q.trim();
    if (!k) return [];
    return dramas.filter(
      (d) =>
        d.title.includes(k) ||
        d.synopsis.includes(k) ||
        d.cast.some((c) => c.includes(k)) ||
        d.genres.some((g) => g.includes(k))
    );
  }, [q]);

  const matchedNews = useMemo(() => {
    const k = q.trim();
    if (!k) return [];
    return newsList.filter(
      (n) => n.title.includes(k) || n.summary.includes(k) || n.content.some((c) => c.includes(k))
    );
  }, [q]);

  const list = type === "news" ? matchedNews : matchedDramas;
  const total = list.length;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(totalPages, 1));
  const dramaPage = matchedDramas.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const newsPage = matchedNews.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (input.trim()) next.set("q", input.trim());
    next.set("type", type);
    setSearchParams(next);
  };

  const switchType = (nextType: "drama" | "news") => {
    const next = new URLSearchParams(searchParams);
    next.set("type", nextType);
    next.delete("page");
    setSearchParams(next);
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <div className="mb-6">
        <Breadcrumb items={[{ name: "首页", path: "/" }, { name: "搜索" }]} />
      </div>

      <header className="mb-6 md:mb-8">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">全站搜索</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600">
          输入关键词，快速查找影视作品与影视资讯
        </p>
      </header>

      <form onSubmit={submit} className="max-w-2xl mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i
              className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"
              aria-hidden="true"
            ></i>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索影视作品、主演、类型或资讯"
              aria-label="搜索关键词"
              className="w-full pl-11 pr-4 py-3 text-sm rounded-full bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <button
            type="submit"
            className="px-7 py-3 rounded-full bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </div>
      </form>

      {q ? (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 bg-background-100 p-1 rounded-full w-fit">
              <button
                type="button"
                onClick={() => switchType("drama")}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  type === "drama" ? "bg-primary-600 text-background-50" : "text-foreground-600"
                }`}
              >
                影视作品（{matchedDramas.length}）
              </button>
              <button
                type="button"
                onClick={() => switchType("news")}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  type === "news" ? "bg-primary-600 text-background-50" : "text-foreground-600"
                }`}
              >
                影视资讯（{matchedNews.length}）
              </button>
            </div>
            <span className="text-sm text-foreground-500">
              关键词“<span className="text-foreground-900 font-medium">{q}</span>”共找到{" "}
              <strong className="text-accent-600">{total}</strong> 条结果
            </span>
          </div>

          {total > 0 ? (
            <>
              {type === "drama" ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {dramaPage.map((drama) => (
                    <DramaCard key={drama.id} drama={drama} />
                  ))}
                </div>
              ) : (
                <div className="space-y-3 md:space-y-4">
                  {newsPage.map((item) => (
                    <NewsCard key={item.id} item={item} />
                  ))}
                </div>
              )}
              <div className="mt-10">
                <Pagination
                  page={safePage}
                  totalPages={totalPages}
                  onChange={(p) => {
                    const next = new URLSearchParams(searchParams);
                    if (p <= 1) next.delete("page");
                    else next.set("page", String(p));
                    setSearchParams(next);
                  }}
                />
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <span className="w-14 h-14 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-2xl mb-4">
                <i className="ri-emotion-sad-line" aria-hidden="true"></i>
              </span>
              <p className="text-foreground-700 font-medium">没有找到与“{q}”相关的内容</p>
              <p className="mt-1 text-sm text-foreground-500">试试更换关键词，或浏览分类与资讯</p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/category/tv"
                  className="px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  浏览电视剧
                </Link>
                <Link
                  to="/"
                  className="px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-10">
          <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">热门搜索</h2>
          <div className="flex flex-wrap gap-2 mb-6">
            {["古装", "悬疑", "科幻", "武侠", "青春", "谍战", "长风渡月", "影视资讯"].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => {
                  setInput(tag);
                  setSearchParams({ q: tag, type: "drama" });
                }}
                className="px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-accent-300 hover:text-accent-600 transition-colors cursor-pointer"
              >
                {tag}
              </button>
            ))}
          </div>
          <p className="text-sm text-foreground-600">
            请输入关键词开始搜索，支持按影视作品标题、主演、类型以及资讯内容进行匹配。
          </p>
        </div>
      )}
    </div>
  );
}